<template>
  <LoadingIndicator v-if="!run && !loadError" message="Loading run..." />
  <div v-else-if="loadError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ loadError }}</div>
  <div v-else class="space-y-6">
    <HermesRecordHeader :back="{ name: 'AdminHermesRuns' }" back-label="All runs" :title="`Run ${run.id}`">
      <template #badges><HermesBadge :value="run.status" /></template>
      <template v-if="run.parent" #meta>Unit of <router-link :to="{ name: 'AdminHermesRun', params: { runId: run.parent } }" class="text-neutral-800">run #{{ run.parent }}</router-link></template>
      <template v-if="live && !confirmCancel" #actions>
        <button type="button" :class="SECONDARY_BUTTON" @click="confirmCancel = true">Cancel run</button>
      </template>
    </HermesRecordHeader>
    <div class="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div v-if="confirmCancel" class="rounded-md bg-neutral-50 px-4 py-3 text-gray-900 flex items-center gap-3">
        <span>Cancel this run? {{ childCount('pending') }} pending units will not start; running units finish and keep their results.</span>
        <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="confirmCancel = false">Keep running</button>
        <button type="button" :class="PRIMARY_BUTTON" @click="cancel">Cancel run</button>
      </div>
      <div v-if="actionError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ actionError }}</div>
      <div v-if="run.status === 'failed'" class="rounded-md bg-red-tint px-4 py-3 text-gray-900"><strong>Run failed.</strong> {{ run.error || 'No error message was recorded.' }}</div>

      <div v-if="progress.units !== undefined">
        <div class="flex h-2 rounded bg-gray-100 overflow-hidden" role="progressbar" :aria-valuenow="progress.done || 0" :aria-valuemax="progress.units">
          <div class="bg-secondary-500" :style="{ width: pct(progress.done - (progress.failed || 0)) }" />
          <div class="bg-gray-900" :style="{ width: pct(progress.failed || 0) }" />
        </div>
        <div class="text-gray-600 mt-2 tabular-nums">
          {{ (progress.done || 0) - (progress.failed || 0) }} completed · {{ progress.failed || 0 }} failed · {{ childCount('running') }} running · {{ childCount('pending') }} pending · {{ progress.units }} units
        </div>
      </div>
      <div v-else-if="progress.turns !== undefined" class="text-gray-700 tabular-nums">
        {{ progress.turns }} turns · {{ progress.results || 0 }} results · {{ effectCount }} effects
      </div>

      <div class="grid grid-cols-12 gap-6">
        <dl class="col-span-6 grid grid-cols-3 gap-y-2">
          <dt class="text-gray-500">Template</dt>
          <dd class="col-span-2">
            <router-link v-if="run.template" :to="{ name: 'AdminHermesTemplate', params: { templateId: run.template } }" class="text-neutral-800">{{ run.template_name }}</router-link>
            <span class="text-gray-500"> @ v{{ run.template_version }}</span>
          </dd>
          <dt class="text-gray-500">Executor</dt>
          <dd class="col-span-2">
            <router-link :to="{ name: 'AdminHermesExecutor', params: { executorKey: run.executor_key } }" class="text-neutral-800">{{ run.executor_key }}</router-link>
            <span class="text-gray-500"> · {{ run.executor_model }} · {{ words(run.executor_runtime) }}</span>
          </dd>
          <dt class="text-gray-500">Trigger</dt>
          <dd class="col-span-2">{{ words(run.trigger) }}<span v-if="run.requested_by_name"> · <span class="sensitive">{{ run.requested_by_name }}</span></span>
            <span v-if="(run.trigger_ref || {}).schedule_id"> · <router-link :to="{ name: 'AdminHermesSchedule', params: { scheduleId: run.trigger_ref.schedule_id } }" class="text-neutral-800">schedule {{ run.trigger_ref.schedule_id }}</router-link></span>
          </dd>
          <template v-if="run.unit_key">
            <dt class="text-gray-500">Unit</dt>
            <dd class="col-span-2"><span class="sensitive">{{ run.unit_key }}</span><span v-if="run.entity_name" class="sensitive text-gray-600"> · {{ run.entity_name }}</span></dd>
          </template>
        </dl>
        <dl class="col-span-6 grid grid-cols-3 gap-y-2">
          <dt class="text-gray-500">Params</dt>
          <dd class="col-span-2 flex flex-wrap gap-2">
            <span v-for="(v, k) in run.params" :key="k" class="rounded bg-gray-100 px-2">{{ k }}={{ v }}</span>
            <span v-if="!Object.keys(run.params || {}).length" class="text-gray-500">No params</span>
          </dd>
          <dt class="text-gray-500">Started</dt><dd class="col-span-2" :title="absolute(run.started_at)">{{ run.started_at ? relative(run.started_at) : (live ? 'Not yet' : '—') }}</dd>
          <dt class="text-gray-500">Finished</dt><dd class="col-span-2" :title="absolute(run.finished_at)">{{ run.finished_at ? relative(run.finished_at) : '—' }}</dd>
        </dl>
      </div>

      <div class="grid grid-cols-4 gap-6 border-t border-gray-200 pt-4">
        <div><div class="text-gray-500">{{ live ? 'Cost so far' : 'Cost' }}</div><div class="text-[16px] font-semibold tabular-nums">{{ money(usage.cost_usd) }}</div></div>
        <div><div class="text-gray-500">Tokens, prompt / completion</div><div class="text-[16px] font-semibold tabular-nums">{{ tokens(usage.prompt_tokens) }} / {{ tokens(usage.completion_tokens) }}</div></div>
        <div><div class="text-gray-500">Model calls</div><div class="text-[16px] font-semibold tabular-nums">{{ usage.calls ?? (usage.cost_usd ? 1 : 0) }}</div></div>
        <div><div class="text-gray-500">{{ live ? 'Elapsed' : 'Duration' }}</div><div class="text-[16px] font-semibold tabular-nums">{{ duration(run.started_at, run.finished_at) || '—' }}</div></div>
      </div>
    </div>

    <HermesCard v-if="(run.metadata || {}).summary" title="Summary" :meta="`${progress.turns || 0} turns`" collapsible>
      <p class="sensitive text-gray-800 whitespace-pre-wrap" data-demo-blur>{{ run.metadata.summary }}</p>
    </HermesCard>

    <div class="bg-white rounded-lg border border-gray-200">
      <nav class="flex gap-6 px-4 border-b border-gray-200">
        <button
v-for="t in detailTabs" :key="t.key" type="button"
                :class="['py-3 font-medium border-b-2 -mb-px', tab === t.key ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900']"
                @click="setTab(t.key)">{{ t.label }} <span class="text-gray-500 tabular-nums">{{ t.count }}</span></button>
      </nav>

      <div v-if="tab === 'units'">
        <div class="flex gap-2 px-4 py-3">
          <button
v-for="s in ['', 'failed', 'running', 'completed', 'pending']" :key="s" type="button"
                  :class="['rounded-full px-3 py-1', unitStatus === s ? 'bg-neutral-100 text-gray-900' : 'bg-gray-50 text-gray-600']"
                  @click="unitStatus = s; loadUnits()">{{ s ? `${words(s)} ${childCount(s)}` : 'All' }}</button>
        </div>
        <table class="w-full table-fixed divide-y divide-gray-200">
          <colgroup><col style="width:8%"><col style="width:13%"><col style="width:15%"><col style="width:10%"><col style="width:7%"><col style="width:17%"><col style="width:22%"><col style="width:8%"></colgroup>
          <thead class="bg-gray-50"><tr>
            <th :class="TH">Unit run</th><th :class="TH">Unit key</th><th :class="TH">Patient</th><th :class="TH">Status</th>
            <th :class="[TH, 'text-right']">Cost</th><th :class="TH">Inputs with data</th><th :class="TH">Error</th><th :class="[TH, 'text-right']">Actions</th>
          </tr></thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="!units.length"><td colspan="8" class="px-3 py-8 text-center text-gray-500">{{ run.status === 'failed' ? 'No units were created: the run failed before fanning out.' : 'No units match.' }}</td></tr>
            <tr v-for="u in units" :key="u.id">
              <td :class="TD"><router-link :to="{ name: 'AdminHermesRun', params: { runId: u.id } }" class="text-neutral-800">#{{ u.id }}</router-link></td>
              <td :class="TD"><span class="sensitive block truncate" :title="u.unit_key">{{ u.unit_key }}</span></td>
              <td :class="TD"><span class="sensitive">{{ u.entity_name || '—' }}</span></td>
              <td :class="TD"><HermesBadge :value="u.status" /></td>
              <td :class="[TD, 'text-right tabular-nums']">{{ money((u.usage || {}).cost_usd) }}</td>
              <td :class="TD">{{ inputsLine(u) }}</td>
              <td :class="TD">{{ u.error }}</td>
              <td :class="[TD, 'text-right whitespace-nowrap']">
                <router-link v-if="(u.metadata || {}).session_id" :to="{ name: 'Agent', params: { sessionId: u.metadata.session_id } }" :class="TEXT_BUTTON" target="_blank">Session</router-link>
                <button v-if="u.status === 'completed'" type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="unitFilter = u.unit_key; setTab('results')">Result</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="unitsNext" class="px-4 py-3 border-t border-gray-200"><button type="button" :class="TEXT_BUTTON" @click="loadUnits(true)">Show more units</button></div>
      </div>

      <div v-else-if="tab === 'results'" class="overflow-x-auto">
        <div v-if="unitFilter" class="px-4 py-3 text-gray-600">Showing unit <span class="sensitive">{{ unitFilter }}</span> <button type="button" :class="[TEXT_BUTTON, 'ml-2']" @click="unitFilter = ''; loadResults()">Show all</button></div>
        <table class="w-full table-fixed divide-y divide-gray-200">
          <colgroup><col style="width:12%"><col style="width:13%"><col v-for="f in resultColumns" :key="f.name" :style="{ width: `${Math.floor(69 / Math.max(resultColumns.length, 1))}%` }"><col style="width:6%"></colgroup>
          <thead class="bg-gray-50"><tr>
            <th :class="TH">Unit key</th><th :class="TH">Patient</th>
            <th v-for="f in resultColumns" :key="f.name" :class="[TH, isNumeric(f) ? 'text-right' : '']">{{ words(f.name) }}</th>
            <th :class="TH" />
          </tr></thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="!results.length"><td :colspan="resultColumns.length + 3" class="px-3 py-8 text-center text-gray-500">No results recorded.</td></tr>
            <template v-for="r in results" :key="r.id">
              <tr :class="expanded === r.id ? 'bg-primary-50' : ''">
                <td :class="[TD, expanded === r.id ? 'border-l-2 border-primary-500' : '']"><span class="sensitive block truncate" :title="r.unit_key">{{ r.unit_key || r.label || '—' }}</span></td>
                <td :class="TD"><span class="sensitive">{{ r.entity_name || '—' }}</span></td>
                <td v-for="f in resultColumns" :key="f.name" :class="[TD, isNumeric(f) ? 'text-right tabular-nums' : '']">
                  <template v-if="(r.data || {})[f.name] === undefined">
                    <span class="text-gray-500">{{ f.required ? 'Not in result' : '—' }}</span>
                  </template>
                  <span v-else-if="isProse(f.name, f.spec, r.data[f.name])" class="sensitive line-clamp-3" data-demo-blur>{{ cellValue(r.data[f.name], f.spec) }}</span>
                  <span v-else>{{ cellValue(r.data[f.name], f.spec) }}</span>
                </td>
                <td :class="[TD, 'text-right']"><button type="button" :class="TEXT_BUTTON" @click="expanded = expanded === r.id ? null : r.id">{{ expanded === r.id ? 'Close' : 'Expand' }}</button></td>
              </tr>
              <tr v-if="expanded === r.id">
                <td :colspan="resultColumns.length + 3" class="px-4 py-3 bg-gray-50">
                  <div class="text-gray-500 mb-2">Result {{ r.id }} · unit run #{{ r.run }} · {{ absolute(r.created_at) }}</div>
                  <pre class="sensitive whitespace-pre-wrap break-words" data-demo-blur>{{ JSON.stringify(r.data, null, 2) }}</pre>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div v-if="resultsNext" class="px-4 py-3 border-t border-gray-200"><button type="button" :class="TEXT_BUTTON" @click="loadResults(true)">Show more results</button></div>
      </div>

      <div v-else-if="tab === 'effects'">
        <table class="w-full table-fixed divide-y divide-gray-200">
          <colgroup><col style="width:12%"><col style="width:9%"><col style="width:11%"><col style="width:36%"><col style="width:18%"><col style="width:14%"></colgroup>
          <thead class="bg-gray-50"><tr>
            <th :class="TH">Kind</th><th :class="TH">Mode</th><th :class="TH">State</th><th :class="TH">Payload</th><th :class="TH">Decided</th><th :class="[TH, 'text-right']">Actions</th>
          </tr></thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="!effects.length"><td colspan="6" class="px-3 py-8 text-center text-gray-500">{{ run.template_name }} made and proposed no writes in this run.</td></tr>
            <tr v-for="e in effects" :key="e.id">
              <td :class="TD">{{ e.kind }}</td>
              <td :class="TD"><HermesBadge :value="e.mode" /></td>
              <td :class="TD"><HermesBadge :value="e.state" /></td>
              <td :class="TD">
                <span class="sensitive">{{ effectSummary(e) }}</span>
                <div v-if="e.ok === false" class="mt-1 rounded bg-red-tint px-2 py-1 text-gray-900">{{ (e.result || {}).error }}</div>
              </td>
              <td :class="TD"><span class="sensitive">{{ e.decided_by_name }}</span> <span :title="absolute(e.decided_at)">{{ relative(e.decided_at) }}</span></td>
              <td :class="[TD, 'text-right whitespace-nowrap']">
                <template v-if="e.state === 'pending'">
                  <button type="button" :class="TEXT_BUTTON" @click="decide(e, true)">Approve</button>
                  <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="decide(e, false)">Decline</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="tab === 'frames'" class="p-4">
        <p v-if="!frames.length" class="text-gray-500">{{ live ? 'Frames are saved when the run finishes.' : 'No frames were saved for this run.' }}</p>
        <div v-else class="grid grid-cols-12 gap-6">
          <div class="col-span-6 rounded-md border border-gray-200 p-4">
            <h3 class="font-semibold text-gray-900 mb-2">Results frame</h3>
            <div v-for="f in frames.filter((x) => !x.part)" :key="f.id">
              {{ f.name }}
              <div class="text-gray-500">Frame {{ f.id }} · {{ f.rows }} rows · {{ f.columns }} columns</div>
            </div>
          </div>
          <div class="col-span-6 rounded-md border border-gray-200 p-4">
            <h3 class="font-semibold text-gray-900 mb-2">Inputs each call saw</h3>
            <p v-if="!frames.some((x) => x.part)" class="text-gray-500">This executor does not capture inputs.</p>
            <div v-for="f in frames.filter((x) => x.part)" :key="f.id" class="mb-2">
              {{ f.name }}
              <div class="text-gray-500">Frame {{ f.id }} · {{ f.rows }} rows</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import HermesRecordHeader from './HermesRecordHeader.vue';
import {
  API, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
  absolute, cellValue, duration, effectSummary, errorOf, isProse, money, relative, schemaFields, tokens, words,
} from './hermes';

export default {
  name: 'HermesRunDetail',
  components: { LoadingIndicator, HermesBadge, HermesCard, HermesRecordHeader },
  data() {
    return {
      run: null, template: null, loadError: '', actionError: '', confirmCancel: false, timer: null,
      tab: this.$route.query.tab || '', units: [], unitsNext: null, unitStatus: '', results: [], resultsNext: null, resultCount: 0,
      unitFilter: '', expanded: null, effects: [], frames: [],
      PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    live() {
      return this.run && ['pending', 'running'].includes(this.run.status);
    },
    progress() {
      return this.run.progress || {};
    },
    usage() {
      return this.run.usage || {};
    },
    fanOut() {
      return Object.keys(this.run.children || {}).length > 0 || this.progress.units !== undefined;
    },
    effectCount() {
      return this.effects.length;
    },
    detailTabs() {
      const tabs = [];
      if (this.fanOut) tabs.push({ key: 'units', label: 'Units', count: Object.values(this.run.children || {}).reduce((a, b) => a + b, 0) });
      tabs.push({ key: 'results', label: 'Results', count: this.run.results }, { key: 'effects', label: 'Effects', count: this.effects.length },
        { key: 'frames', label: 'Frames', count: this.frames.length });
      return tabs;
    },
    resultColumns() {
      const fields = schemaFields(this.template && this.template.result_schema);
      if (fields.length) return fields.filter((f) => f.spec.type !== 'object' || (f.spec.properties || {}).text).slice(0, 8);
      const keys = new Set();
      this.results.forEach((r) => Object.keys(r.data || {}).forEach((k) => keys.add(k)));
      return [...keys].slice(0, 8).map((name) => ({ name, spec: {}, required: false }));
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    absolute, cellValue, duration, effectSummary, isProse, money, relative, tokens, words,
    async load() {
      clearTimeout(this.timer);
      try {
        const id = this.$route.params.runId;
        const [run, effects, frames] = await Promise.all([
          this.$axios.get(`${API}/runs/${id}/`), this.$axios.get(`${API}/runs/${id}/effects/`, { params: { limit: 500 } }),
          this.$axios.get(`${API}/runs/${id}/frames/`),
        ]);
        const first = !this.run;
        this.run = run.data;
        this.effects = effects.data.results;
        this.frames = frames.data;
        if (first && this.run.template) this.template = (await this.$axios.get(`${API}/templates/${this.run.template}/`)).data;
        if (!this.tab) this.tab = this.fanOut ? 'units' : 'results';
        if (first || this.live) this.refreshTab();
        if (this.live) this.timer = setTimeout(this.load, 3000);
      } catch (e) {
        this.loadError = e?.response?.status === 404 ? 'No run with that id on this tenant.' : errorOf(e, 'The run could not be loaded.');
      }
    },
    refreshTab() {
      if (this.tab === 'units') this.loadUnits();
      if (this.tab === 'results') this.loadResults();
    },
    setTab(key) {
      this.tab = key;
      this.$router.replace({ query: { ...this.$route.query, tab: key } });
      this.refreshTab();
    },
    childCount(status) {
      return (this.run.children || {})[status] || 0;
    },
    pct(n) {
      return `${(100 * (n || 0)) / Math.max(this.progress.units || 1, 1)}%`;
    },
    inputsLine(u) {
      const present = (u.metadata || {}).inputs_present;
      const labels = ((this.template || {}).inputs || []).map((i) => i.label);
      if (!present || !labels.length) return '';
      const missing = labels.filter((l) => !present.includes(l));
      return `${present.length} of ${labels.length}${missing.length ? ` · no data: ${missing.join(', ')}` : ''}`;
    },
    isNumeric(f) {
      return ['integer', 'number'].includes(f.spec.type);
    },
    async loadUnits(more = false) {
      const params = { parent: this.run.id, limit: 100 };
      if (this.unitStatus) params.status = this.unitStatus;
      const { data } = await this.$axios.get(more && this.unitsNext ? this.unitsNext : `${API}/runs/`, more ? {} : { params });
      const order = { failed: 0, running: 1, completed: 2, pending: 3, cancelled: 4 };
      const rows = more ? this.units.concat(data.results) : data.results;
      this.units = rows.sort((a, b) => (order[a.status] - order[b.status]) || (a.id - b.id));
      this.unitsNext = data.next;
    },
    async loadResults(more = false) {
      const params = { limit: 100 };
      if (this.unitFilter) params.unit_key = this.unitFilter;
      const { data } = await this.$axios.get(more && this.resultsNext ? this.resultsNext : `${API}/runs/${this.run.id}/results/`, more ? {} : { params });
      this.results = more ? this.results.concat(data.results) : data.results;
      this.resultsNext = data.next;
    },
    async cancel() {
      this.actionError = '';
      try {
        await this.$axios.post(`${API}/runs/${this.run.id}/cancel/`);
        this.confirmCancel = false;
        this.load();
      } catch (e) {
        this.actionError = errorOf(e, 'The run could not be cancelled.');
      }
    },
    async decide(e, approved) {
      this.actionError = '';
      try {
        await this.$axios.post(`${API}/effects/${e.id}/decide/`, { approved });
      } catch (err) {
        this.actionError = errorOf(err, 'The decision was not recorded.');
      }
      this.load();
    },
  },
};
</script>
